import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { PostCard } from "@/pages/Blog";
import { BlogBookingBox } from "@/components/BlogBookingBox";
import { ShareButtons } from "@/components/ShareButtons";

export default function BlogPost() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setP(null); setMissing(false);
    api.get(`/blog/${slug}`).then((r) => setP(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return (
    <div className="jt-container py-20" data-testid="blog-post-missing">
      <Seo title="Post not found" noindex/>
      <h1 className="font-serif text-3xl text-[#1E3A2B] mb-4">This post isn&apos;t available</h1>
      <Link to="/blog" className="jt-pill-primary">Back to the journal</Link>
    </div>
  );
  if (!p) return <div className="jt-container py-20">Loading…</div>;

  const description = p.meta_description || p.excerpt;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, description,
    image: p.cover_image ? [p.cover_image.startsWith("/") ? window.location.origin + p.cover_image : p.cover_image] : undefined,
    datePublished: p.publish_at, dateModified: p.updated_at, keywords: p.tags.join(", "),
    author: { "@type": "Organization", name: p.author },
    publisher: { "@type": "Organization", name: "JawaiTrails", logo: { "@type": "ImageObject", url: `${window.location.origin}/logo-dark.png` } },
    mainEntityOfPage: window.location.href,
  };

  return (
    <article className="pb-20" data-testid="blog-post">
      <Seo title={p.meta_title || p.title} description={description} image={p.cover_image} type="article" jsonLd={jsonLd}/>
      {p.cover_image && (
        <div className="h-[42vh] min-h-[280px] w-full overflow-hidden bg-[#EFE9E1]">
          <img src={p.cover_image} alt={p.cover_alt || p.title} className="w-full h-full object-cover" data-testid="blog-post-cover"/>
        </div>
      )}
      <div className="jt-container -mt-16 relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px] items-start">
        <div className="bg-[#FBF8F4] rounded-2xl p-6 md:p-10 shadow-sm min-w-0">
          <nav className="text-xs text-[#5C6B63] mb-4" aria-label="Breadcrumb"><Link to="/blog" className="hover:text-[#1E3A2B]">Journal</Link> / <span>{p.title}</span></nav>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1E3A2B] leading-tight" data-testid="blog-post-title">{p.title}</h1>
          <div className="text-sm text-[#5C6B63] mt-4 flex gap-3 flex-wrap">
            <span>{p.author}</span><span>·</span>
            <time dateTime={p.publish_at}>{format(new Date(p.publish_at), "dd MMMM yyyy")}</time><span>·</span><span>{p.reading_minutes} min read</span>
          </div>
          <div className="mt-5"><ShareButtons post={p} id="share-top"/></div>
          <div className="jt-prose mt-8" data-testid="blog-post-body" dangerouslySetInnerHTML={{ __html: p.content_html }}/>
          <div className="flex gap-2 flex-wrap mt-8">{p.tags.map((t) => <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="text-xs px-3 py-1 rounded-full bg-[#F0E8DE] text-[#1E3A2B]" data-testid={`blog-tag-${t}`}>#{t}</Link>)}</div>
          <div className="mt-10 rounded-2xl bg-[#1E3A2B] text-white p-6 md:p-8" data-testid="blog-cta">
            <div className="font-serif text-2xl">Ready to see Jawai for yourself?</div>
            <p className="text-sm text-white/70 mt-1 mb-5">Private jeeps, expert naturalists, and pickup from your hotel. <Link to="/safaris" className="underline" data-testid="blog-cta-book">See all safaris</Link></p>
            <div className="text-[#1C2421]"><BlogBookingBox testid="blog-book-end"/></div>
          </div>
          <div className="mt-8"><ShareButtons post={p} id="share-bottom"/></div>
        </div>
        <aside className="hidden lg:block sticky top-28 mt-20" data-testid="blog-book-aside"><BlogBookingBox testid="blog-book-side" compact/></aside>
        {p.related?.length > 0 && (
          <section className="mt-6 lg:col-span-2" data-testid="blog-related">
            <h2 className="font-serif text-lg text-[#1E3A2B] mb-5">Keep reading</h2>
            <div className="grid gap-6 md:grid-cols-3">{p.related.map((r) => <PostCard key={r.id} p={r}/>)}</div>
          </section>
        )}
      </div>
    </article>
  );
}
